/**
 * Anonymous identity recovery — turns the anon-uid-change DETECTION
 * (anonCredentialBackup + the previouslyKnown check in Auth.js) into a full
 * recovery: when a returning guest gets a fresh anonymous uid because the
 * device's auth storage was wiped (iOS ITP, in-app browsers), we link the two
 * identities durably and carry their stuff across, so to the guest — and to
 * the venue's loyalty tracking — they never "become someone else".
 *
 * What happens on detection (see ANONYMOUS_USER_PORTABILITY.md):
 *   1. Durable link:  users/{new}.previousAnonUids += old
 *                     users/{old}.supersededBy = new  (+ supersededAt)
 *   2. Points move:   global users.points + redeemedTiers badges, and every
 *                     per-venue venuePoints/{venue}__{old} balance, transfer
 *                     onto the new uid; old balances are zeroed and stamped
 *                     (migratedTo) so nothing double-counts and the venue's
 *                     Loyalty Rewards leaderboard shows ONE guest, not two.
 *   3. History copy:  users/{old}/requests → users/{new}/requests (capped).
 *   4. Identity keep: the caller reuses the OLD displayName + photoURL, so
 *                     the guest keeps the same name and avatar face.
 *
 * Everything is best-effort and idempotent (re-running on the same pair is
 * a no-op for the value transfers) — a recovery hiccup must never break
 * sign-in, matching the pointsService error posture.
 */

import {
  doc, getDoc, getDocs, setDoc, collection, query, where, limit,
  writeBatch, increment, arrayUnion, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase-config.js';

/**
 * Start recovery for a detected uid change. Reads the old profile QUICKLY and
 * returns { displayName, photoURL } so the sign-in flow can keep the guest's
 * old identity; the value/history migration continues in the background.
 * Returns null when there's nothing to recover.
 */
export async function startAnonRecovery(oldUid, newUid) {
  if (!oldUid || !newUid || oldUid === newUid) return null;
  const oldSnap = await getDoc(doc(db, 'users', oldUid));
  if (!oldSnap.exists()) return null;
  const old = oldSnap.data() || {};

  // Only ever recover GUEST identities — if the stale backup somehow points
  // at a real signed-in account, leave it alone entirely.
  if (old.email) return null;

  // If this old identity already migrated (to us or to an even newer uid),
  // don't move value twice — but still return the identity so the face/name
  // stay consistent when it migrated to us.
  const alreadyMigrated = !!old.pointsMigratedTo;

  _migrate(oldUid, newUid, old, alreadyMigrated).catch((e) =>
    console.warn('[anonRecovery] migration failed:', e?.message)
  );

  return {
    displayName: old.displayName || null,
    photoURL: old.photoURL || null,
  };
}

async function _migrate(oldUid, newUid, old, alreadyMigrated) {
  const newRef = doc(db, 'users', newUid);
  const oldRef = doc(db, 'users', oldUid);

  // 1. Durable two-way link (idempotent: arrayUnion + same-value merges).
  await setDoc(newRef, { previousAnonUids: arrayUnion(oldUid) }, { merge: true });
  await setDoc(oldRef, { supersededBy: newUid, supersededAt: serverTimestamp() }, { merge: true });
  if (alreadyMigrated) return;

  // 2a. Global points + tier badges.
  const pts = Number(old.points) || 0;
  const tiers = Array.isArray(old.redeemedTiers) ? old.redeemedTiers.filter(Number.isFinite) : [];
  const patch = {};
  if (pts > 0) patch.points = increment(pts);
  if (tiers.length) patch.redeemedTiers = arrayUnion(...tiers);
  if (Object.keys(patch).length) await setDoc(newRef, patch, { merge: true });
  await setDoc(oldRef, { points: 0, pointsMigratedTo: newUid }, { merge: true });

  // 2b. Per-venue balances — move value + lifetime history + claimed tiers,
  // then zero the old rows so venue leaderboards show one guest.
  try {
    const vpSnap = await getDocs(query(collection(db, 'venuePoints'), where('uid', '==', oldUid)));
    for (const d of vpSnap.docs) {
      const v = d.data() || {};
      if (!v.venueUid || v.migratedTo) continue;
      const vPatch = {
        venueUid: v.venueUid,
        uid: newUid,
        points: increment(Number(v.points) || 0),
        lifetimeEarned: increment(Number(v.lifetimeEarned) || 0),
        lastEarnedAt: v.lastEarnedAt || serverTimestamp(),
      };
      const vTiers = Array.isArray(v.redeemedTiers) ? v.redeemedTiers.filter(Number.isFinite) : [];
      if (vTiers.length) vPatch.redeemedTiers = arrayUnion(...vTiers);
      await setDoc(doc(db, 'venuePoints', `${v.venueUid}__${newUid}`), vPatch, { merge: true });
      await setDoc(d.ref, { points: 0, lifetimeEarned: 0, migratedTo: newUid }, { merge: true });
    }
  } catch (e) {
    console.warn('[anonRecovery] venuePoints transfer failed:', e?.message);
  }

  // 3. Request history (capped — enough for the profile's recent list).
  try {
    const reqSnap = await getDocs(query(collection(db, 'users', oldUid, 'requests'), limit(200)));
    let batch = writeBatch(db);
    let n = 0;
    for (const r of reqSnap.docs) {
      batch.set(doc(db, 'users', newUid, 'requests', r.id), r.data(), { merge: true });
      if (++n >= 400) { await batch.commit(); batch = writeBatch(db); n = 0; }
    }
    if (n) await batch.commit();
  } catch (e) {
    console.warn('[anonRecovery] request-history copy skipped:', e?.message);
  }

  console.log(`[anonRecovery] ${oldUid} → ${newUid} recovered (points, venue balances, history)`);
}
